/**
 * useNotificationEvents — subscribe the signed-in user's `notifications:{id}`
 * channel (WP-E).
 *
 * This channel has existed, and been authorized, since the realtime substrate
 * shipped: `notificationChannel()` was exported and `apis/realtime`'s policy
 * has always allowed `notifications:*` for an authenticated caller. Nothing
 * ever subscribed it, so the per-user events the server published reached
 * nobody, and the Messages tab badge only refreshed while a conversation
 * thread happened to be open — the one place a `conversations:{id}`
 * subscription existed.
 *
 * Subscribing is the whole fix. The provider already mounts the central
 * envelope bridge (`useRealtimeQueryBridge`), which applies the declared
 * query invalidations for EVERY envelope on ANY subscribed channel — so once
 * this channel is live, `conversationMessageReceived: invalidateUnreadCount`
 * fires for messages that arrive while the user is anywhere in the app.
 *
 * Note on what the envelope carries (WP-C): `unread_count` on this channel is
 * PER-CONVERSATION. The tab badge counts CONVERSATIONS WITH UNREAD. They are
 * different numbers and must never be assigned to one another, which is why
 * this hook invalidates the badge query rather than writing a value into it.
 */
import { notificationChannel, useSubscription } from '@letsgethome/realtime';

import { useAuth } from '../providers/AuthProvider';

/**
 * Subscribe the authenticated user's notification channel.
 *
 * A no-op while signed out: a signed-out user has no notification channel, so
 * the hook passes `null` (H-4) — `useSubscription` opens nothing and requests
 * no token. No empty-string channel is invented.
 */
export function useNotificationEvents(): { isSubscribed: boolean } {
  const { user } = useAuth();

  return useSubscription(
    user ? notificationChannel(user.id) : null,
    // The envelope needs no per-message handling here: the provider's bridge
    // applies the declared invalidations centrally. Subscribing is what makes
    // those bindings fire at all.
    () => {}
  );
}
